import React, { useEffect, useState, useMemo } from "react";
import Handler from "../../Helpers/Handler";
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter, Input,
  Spinner
} from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";
import "boxicons/css/boxicons.min.css";
import { loadingFalse, loadingTrue } from "../../Reducer/loaderSlice";
import { useDispatch, useSelector } from "react-redux";

/* ---------------- DELIVERY STATUSES ---------------- */
const DELIVERY_FLOW = [
  "accepted",
  "preparing",
  "ready",
  "on_the_way",
  "delivered",
  "cancelled"
];

const filterFlow = [
  "placed",
  "accepted",
  "preparing",
  "ready",
  "on_the_way",
  "delivered",
  "cancelled"
];

const OrdersList = () => {
  const formatIST = (utcTime) => {
    if (!utcTime) return "-";

    // MySQL datetime → ISO + UTC
    const iso = utcTime.replace(" ", "T") + "Z";

    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };


  const dispatch = useDispatch()
  const loading = useSelector((state) => state?.loading?.loading);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [nextStatus, setNextStatus] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  /* optional delivery fields */
  const [remark, setRemark] = useState("");
  const [extraInfo, setExtraInfo] = useState("");
  const [courName, setCourName] = useState("");

  /* search & filter */
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  /* ---------------- LOAD ORDERS ---------------- */
  const loadOrders = async () => {
    const res = await Handler({ method: "get", url: "/order/list.php" });

    if (res?.success) {
      const fixed = res.orders.map((o) => ({
        ...o,
        total: o.items.reduce((sum, it) => sum + Number(it.subtotal), 0),
        payment_status: o.status,
        delivery_status: o.delivery_status
      }));
      setOrders(fixed);
    } else {
      toast.error("Unable to load orders");
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  /* ---------------- FILTER ---------------- */
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const txt =
        `${o.order_id} ${o.customer_name} ${o.customer_mobile}`.toLowerCase();

      const matchSearch = txt.includes(search.toLowerCase());
      const matchStatus =
        statusFilter === "all"
          ? true
          : o.delivery_status === statusFilter;

      return matchSearch && matchStatus;
    });
  }, [orders, search, statusFilter]);

  /* ---------------- HANDLE STATUS CLICK ---------------- */
  const handleStatusClick = async (order, status) => {
    setSelectedOrder(order);
    setNextStatus(status);

    // ✅ ONLY delivered opens modal
    if (status === "delivered") {
      setRemark("");
      setExtraInfo("");
      setModalOpen(true);
      return;
    }

    // ❌ other statuses → direct update (no remark)
    dispatch(loadingTrue())
    const res = await Handler({
      method: "post",
      url: "/delivery_status/delivery_update.php",
      data: {
        order_id: order.order_id,
        status
      }
    });

    if (res?.success) {
      dispatch(loadingFalse())
      toast.success("Status updated");
      loadOrders();
    } else {
      toast.error("Failed to update status");
    }
  };

  /* ---------------- CONFIRM DELIVERED ---------------- */
  const confirmDelivered = async () => {

    if (!selectedOrder) return;
    dispatch(loadingTrue())
    if (courName) {
      const sendRes = await Handler({
        method: "post",
        url: "/delivery_status/send_tracking.php",
        data: {
          order_id: selectedOrder.order_id,
          courier_name: courName,
          tracking_id: remark,
          tracking_url: extraInfo
        }
      });
      if (sendRes?.success) {
        dispatch(loadingFalse())
        toast.success('Infomation Send Successfully')
      } else {
        dispatch(loadingFalse())
        toast.error(sendRes?.message);
      }
      if (sendRes?.success) {
        dispatch(loadingTrue())
        const res = await Handler({
          method: "post",
          url: "/delivery_status/delivery_update.php",
          data: {
            order_id: selectedOrder.order_id,
            status: "delivered",
            remark,
            extra_info: extraInfo
          }
        });
        if (res?.success) {
          dispatch(loadingFalse())
          toast.success("Order delivered");
          setModalOpen(false);
          loadOrders();
        } else {
          dispatch(loadingFalse())
          toast.error("Failed to mark delivered");
        }
      }
    } else {
      dispatch(loadingTrue())
      const res = await Handler({
        method: "post",
        url: "/delivery_status/delivery_update.php",
        data: {
          order_id: selectedOrder.order_id,
          status: "delivered",
          remark,
          extra_info: extraInfo
        }
      });

      if (res?.success) {
        dispatch(loadingFalse())
        toast.success("Order delivered");
        setModalOpen(false);
        loadOrders();
      } else {
        loadingFalse()
        toast.error("Failed to mark delivered");
      }
    }

  };

  /* ---------------- MARK AS PAID ---------------- */
  const markAsPaid = async (order) => {
    dispatch(loadingFalse())
    const res = await Handler({
      method: "post",
      url: "/order/update_status.php",
      data: {
        order_id: order.order_id,
        status: "paid"
      }
    });

    if (res?.success) {
      dispatch(loadingFalse())
      toast.success("Payment marked as PAID");
      loadOrders();
    } else {
      dispatch(loadingFalse())
      toast.error("Failed to update payment");
    }
  };

  /* ---------------- PAYMENT BADGE ---------------- */
  const PaymentBadge = ({ order }) => (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span className="badge bg-secondary px-3 py-2">COD</span>

      {order.payment_status === "paid" ? (
        <span className="badge bg-success px-3 py-2">Paid</span>
      ) : (
        <span className="badge bg-warning text-dark px-3 py-2">Unpaid</span>
      )}

      {order.payment_status !== "paid" && (
        <Button
          size="sm"
          color="success"
          className="rounded-pill px-3 py-1"
          onClick={() => markAsPaid(order)}
        >
          Mark as Paid
        </Button>
      )}
    </div>
  );

  /* ---------------- STATUS BUTTONS ---------------- */
  const StatusButtons = ({ order }) => {
    const currentIndex = DELIVERY_FLOW.indexOf(order.delivery_status);

    // If already completed → no buttons
    if (["delivered", "cancelled"].includes(order.delivery_status)) {
      return null;
    }

    return (
      <div className="d-flex flex-wrap gap-2 mt-2">
        {DELIVERY_FLOW.map((status, index) => {
          // previous statuses
          if (index < currentIndex) {
            return (
              <Button
                key={status}
                size="sm"
                color="secondary"
                disabled
                className="rounded-pill px-3 text-capitalize"
              >
                {status.replace(/_/g, " ")}
              </Button>
            );
          }

          // current status
          if (index === currentIndex) {
            return (
              <Button
                key={status}
                size="sm"
                color="dark"
                disabled
                className="rounded-pill px-3 text-capitalize"
              >
                {status.replace(/_/g, " ")}
              </Button>
            );
          }

          // future statuses
          return (
            <Button
              key={status}
              size="sm"
              disabled={loading}
              color="primary"
              className="rounded-pill px-3 text-capitalize fw-bold"
              onClick={() => handleStatusClick(order, status)}
            >
              {status.replace(/_/g, " ")}
            </Button>
          );
        })}
      </div>
    );
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="container py-4">

      <h3 className="fw-bold mb-3">
        <i className="bx bx-cart me-2"></i> Orders
      </h3>

      {/* SEARCH + FILTER */}
      <div className="row g-2 mb-4">
        <div className="col-md-8">
          <Input
            placeholder="Search by Order ID / Name / Mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Input
            type="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option className="text-capitalize" value="all">All</option>
            {filterFlow.map((s) => (
              <option className="text-capitalize" key={s} value={s}>
                {s.replace(/_/g, " ")}
              </option>
            ))}
          </Input>
        </div>
      </div>

      {filteredOrders && filteredOrders?.map((order) => (
        <div
          key={order.order_id}
          className="p-4 mb-4 shadow-lg rounded-4"
          style={{ background: "#fff", border: "1px solid #eee" }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="fw-bold mb-0">
              #{order.order_id}
              <span className="ms-2 badge bg-dark px-3 py-2">
                {order.table_no}
              </span>
            </h5>
            <span className="text-muted small">
              {formatIST(order.created_at)}
            </span>
          </div>

          <div className="mt-3 d-flex gap-4 flex-wrap">
            <div><strong>Name:</strong> {order.customer_name}</div>
            <div><strong>Mobile:</strong> {order.customer_mobile}</div>
            <PaymentBadge order={order} />
          </div>

          {/* ITEMS (UNTOUCHED) */}
          <div className="mt-4">
            <strong>Items ({order.total_items})</strong>
            <div className="row mt-2">
              {order.items.map((x) => (
                <div key={x.item_id} className="col-md-4 mb-2">
                  <div
                    className="p-2 rounded-3"
                    style={{ background: "#f8f9fa", border: "1px solid #e2e2e2" }}
                  >
                    <div className="fw-bold text-capitalize">{x.name}</div>
                    <div className="text-muted small">
                      Qty: {x.qty} — ₹{x.subtotal}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 fw-bold fs-4 text-success">
            ₹{order.total}
          </div>

          <div className="mt-4">
            <strong>Delivery Status:</strong>
            <StatusButtons order={order} />
          </div>
          {order.delivery_status === "delivered" && (
            <div className="mt-4">
              <strong>Delivery Address: <i className="mx-2">{order?.delivery_address} </i> </strong>
            </div>
          )}
          <div className="mt-3">
            <strong>Current Status :</strong>{" "}
            <span className="badge bg-info text-capitalize text-dark px-3 py-2">
              {order?.delivery_status.replace(/_/g, " ")}
            </span>
          </div>
        </div>
      ))}

      {/* DELIVERY MODAL */}
      <Modal isOpen={modalOpen} centered>
        <ModalHeader>Mark as Delivered</ModalHeader>
        <ModalBody>
          <Input
            className="mb-2"
            placeholder="Name"
            value={courName}
            onChange={(e) => setCourName(e.target.value)}
          />
          <Input
            className="mb-2"
            placeholder="Remark (optional)"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
          <Input
            placeholder="Extra Info (optional)"
            value={extraInfo}
            onChange={(e) => setExtraInfo(e.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button color="primary" disabled={loading} onClick={confirmDelivered} className="d-flex align-items-center justify-content-center gap-2">
            {loading && (
              <Spinner size="sm" color="light" />
            )}
            Confirm Delivered
          </Button>
          {/* <Button color="success" onClick={confirmDelivered}>
            Confirm Delivered
          </Button> */}
        </ModalFooter>
      </Modal>

    </div>
  );
};

export default OrdersList;
